import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';
import { GoShieldCheck } from 'react-icons/go';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { getSalePriceForListing, getSalePriceFromMajorUnit } from '../../util/saleCampaigns';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { GoInfo } from 'react-icons/go';
import { FaCheck } from 'react-icons/fa6';

import {
  ModalInMobile,
  PrimaryButton,
  AvatarSmall,
  H1,
  H2,
  AvatarMedium,
  NamedLink,
} from '../../components';
import { useCookies } from 'react-cookie';
import css from './OrderPanel.module.css';
import {
  getDefaultColor,
  getDefaultCondition,
  getmarketplaceData,
  // , marketplaceData
} from '../../marketplace-data';

const POP_NOUVELLE_USER = process.env.REACT_APP_POP_NOUVELLE_USER;

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();
const popNouvelleUser = process.env.REACT_APP_POP_NOUVELLE_USER;
const maxPrice = 50000;
const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  // console.log(onSubmit, 'sdsd');
  return isInquiryWithoutPayment ? () => onSubmit({}) : () => onSubmit();
  // : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    handleShowBuyerFeesModal,
    isPopnouvelleUser = false,
    salePrice = null,
  } = props;

  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  const salePriceData = salePrice ? priceData(salePrice, marketplaceCurrency, intl) : {};
  const { formattedPrice: formattedSalePrice, priceTitle: salePriceTitle } = salePriceData;
  const hasSalePrice = !!formattedSalePrice && !!formattedPrice;
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      {hasSalePrice ? (
        <div className={css.salePriceContainer}>
          <div className={css.originalPriceInCTA} title={priceTitle}>
            {formattedPrice}
          </div>
          <div className={classNames(css.priceValue, css.salePrice)} title={salePriceTitle}>
            {formattedSalePrice}
          </div>
        </div>
      ) : (
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice}
        </div>
      )}
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
        {!isPopnouvelleUser ? (
          <GoInfo className={css.info} onClick={() => handleShowBuyerFeesModal(true)} />
        ) : null}
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      {hasSalePrice ? (
        <div className={css.salePriceContainer}>
          <p className={css.originalPrice}>{formattedPrice}</p>
          <p className={classNames(css.price, css.salePrice)} title={salePriceTitle}>
            {formattedSalePrice}
          </p>
        </div>
      ) : (
        <p className={css.price} title={priceTitle}>
          {formatMoney(intl, price)}
        </p>
      )}
      <div className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
        {!isPopnouvelleUser ? (
          <GoInfo className={css.info} onClick={() => handleShowBuyerFeesModal(true)} />
        ) : null}
      </div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    currentUser,
    handleShowCondition,
    handleShowAuth,
    handleShowBusinessToCustomer,
    handleShowBuyerFeesModal,
    salePriceOverride,
    useSalePriceOverride,
  } = props;
  const locale = intl.locale;
  const authorId = author?.id?.uuid;
  // console.log('authorId', authorId);

  const publicData = listing?.attributes?.publicData || {};
  const salePrice = useSalePriceOverride
    ? getSalePriceFromMajorUnit(listing, salePriceOverride, marketplaceCurrency)
    : getSalePriceForListing(listing, marketplaceCurrency);
  const isListingClosed = listing?.attributes?.state === 'closed';
  const {
    subCategory: product,
    size,
    designer: brand,
    department: category,
    category: subCategory,
    gender,
    colour,
    otherBrand,
    condition,
    created_by,
  } = publicData || {};
  const [cookies] = useCookies(['language']);
  const marketplaceData = getmarketplaceData(cookies?.language);
  const colorOptions = getDefaultColor(cookies?.language);
  const conditionOptions = getDefaultCondition(cookies?.language);
  const isPopnouvelleUser = authorId === popNouvelleUser || created_by == 'script';
  const isCreatedByScript = created_by == 'script';
  const data = marketplaceData;
  const categoryData = data?.find(d => d.value === category);
  const genderData = categoryData?.gender;
  const subCategoryData = gender
    ? genderData?.enumOptions?.find(d => d.value === gender)?.subCategories?.enumOptions
    : categoryData?.subCategories?.enumOptions;
  const productData = subCategoryData?.find(d => d.value === subCategory)?.products?.enumOptions;
  const productObject = product ? productData?.find(d => d.value === product) : null;
  const brandObject = brand
    ? productObject?.brands?.enumOptions?.find(d => d.value === brand)
    : null;
  const ifOtherBrand = brandObject?.value === 'other' ? otherBrand : brandObject?.label;
  const conditionLabel = condition
    ? conditionOptions?.find(i => i.value === condition)?.label
    : null;

  const sizeObject = size ? productObject?.sizes?.enumOptions?.find(d => d.value === size) : null;

  const colourObject = colour ? colorOptions?.find(i => i.value === colour) : null;

  const { listingType, unitType, transactionProcessAlias = '' } = publicData || {};
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;
  const isPriceGreaterOrEqual = price?.amount >= maxPrice;
  const showPriceMissing = isPaymentProcess && !price;

  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };
  const showAuthentication = isPopnouvelleUser ? (
    <div className={css.authentication}>
      <GoShieldCheck className={css.icon} />{' '}
      <span>{intl.formatMessage({ id: 'OrderPanel.OrderPanel.authenticationIncluded' })}</span>
    </div>
  ) : !isPopnouvelleUser && isPriceGreaterOrEqual ? (
    <div className={css.authentication}>
      <GoShieldCheck className={css.icon} />{' '}
      <span>{intl.formatMessage({ id: 'OrderPanel.OrderPanel.authenticationIncludedB2C' })}</span>
    </div>
  ) : null;
  // <div className={css.authentication}>
  //   <GoShieldCheck className={css.icon} />{' '}
  //   <span>{intl.formatMessage({ id: 'OrderPanel.OrderPanel.authenticationIncluded' })}</span>
  // </div>

  const business2Customer = (
    <div className={css.checkContainer}>
      <p className={css.auth}>
        <span>
          <div className={css.authContainer}>
            <FaCheck className={css.check} />
            <FormattedMessage
              id="OrderPanel.OrderPanel.authAndQualityControl"
              values={{ br: <br /> }}
            />
            {locale === 'en-US' && (
              <span className={css.moreInfoUS} onClick={() => handleShowAuth(true)}>
                {intl.formatMessage({ id: 'OrderPanel.OrderPanel.moreInfo' })}
              </span>
            )}
          </div>
        </span>
        {locale !== 'en-US' && (
          <span className={css.moreInfoA} onClick={() => handleShowAuth(true)}>
            {intl.formatMessage({ id: 'OrderPanel.OrderPanel.moreInfo' })}
          </span>
        )}
      </p>
      <p className={css.auth}>
        <span>
          <FaCheck className={css.check} />
          <FormattedMessage id="OrderPanel.b2c.freeShipping" />
        </span>
      </p>
      <p className={css.auth}>
        <span>
          <FaCheck className={css.check} />
          <FormattedMessage id="OrderPanel.b2c.returnPolicy" />
        </span>
      </p>
      <p className={css.auth}>
        <span>
          <div className={css.authContainer}>
            <FaCheck className={css.check} />
            <FormattedMessage id="OrderPanel.b2c.continuousSupport" />
          </div>
        </span>
        <span className={css.moreInfoA} onClick={() => handleShowBusinessToCustomer(true, false)}>
          {intl.formatMessage({ id: 'OrderPanel.OrderPanel.moreInfo' })}
        </span>
      </p>
    </div>
  );

  // Define the customer-to-customer component
  const customer2Customer = (
    <div className={css.checkContainer}>
      <p className={css.auth}>
        <span>
          <div className={css.authContainer}>
            <FaCheck className={css.check} />
            {isPriceGreaterOrEqual ? (
              <FormattedMessage id="OrderPanel.c2c.authenticity" values={{ br: <br /> }} />
            ) : (
              <FormattedMessage id="OrderPanel.c2c.optionalAuthenticity" values={{ br: <br /> }} />
            )}
            {locale === 'en-US' && (
              <span className={css.moreInfoUS} onClick={() => handleShowAuth(true)}>
                {intl.formatMessage({ id: 'OrderPanel.OrderPanel.moreInfo' })}
              </span>
            )}
          </div>
        </span>
        {locale !== 'en-US' && (
          <span className={css.moreInfoA} onClick={() => handleShowAuth(true)}>
            {intl.formatMessage({ id: 'OrderPanel.OrderPanel.moreInfo' })}
          </span>
        )}
      </p>
      <p className={css.auth}>
        <span>
          <div className={css.authContainer}>
            <FaCheck className={css.check} />
            <FormattedMessage id="OrderPanel.c2c.moneyBackGuarantee" />
          </div>
        </span>
      </p>
      {!isPriceGreaterOrEqual && (
        <p className={css.auth}>
          <span>
            <FaCheck className={css.check} />
            <FormattedMessage id="OrderPanel.c2c.returnWithin72Hours" />
          </span>
        </p>
      )}
      <p className={css.auth}>
        <span>
          <FaCheck className={css.check} />
          <FormattedMessage id="OrderPanel.c2c.safeDelivery" />
        </span>
      </p>
      <p className={css.auth}>
        <span>
          <FaCheck className={css.check} />
          <FormattedMessage id="OrderPanel.c2c.continuousSupport" />
        </span>
        <span className={css.moreInfoA} onClick={() => handleShowBuyerFeesModal(true, false)}>
          {intl.formatMessage({ id: 'OrderPanel.OrderPanel.moreInfo' })}
        </span>
      </p>
    </div>
  );

  const bottomAuthentication = isPopnouvelleUser ? business2Customer : customer2Customer;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = isPurchase && typeof currentStock === 'number';

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = true;
  //  displayDeliveryShipping(listingTypeConfig);

  const displayPickup = displayDeliveryPickup(listingTypeConfig);

  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;
  // console.log('isOrderOpen-------', isOrderOpen);

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;
  const { firstName, lastName } = author?.attributes?.profile?.publicData || {};
  // console.log("firstName----",firstName)
  let authorDisplayName = null;

  if (author?.id?.uuid == process.env.REACT_APP_POP_NOUVELLE_USER) {
    if (firstName && lastName) {
      authorDisplayName = `${firstName} ${lastName}`;
    }
  } else {
    authorDisplayName = userDisplayNameAsString(author, '');
  }
  // console.log("authr----",
  const { username } = author?.attributes?.profile?.metadata || {};
  const userName = username ? username : 'userNameNotPresent';

  // = userDisplayNameAsString(author, '');
  // authorDisplayName
  // author.attributes.profile.publicData.firstName
  // const authorFirstName = author.attributes.profile.firstName;

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <div>
            {brandObject ? <H1 className={css.heading}>{ifOtherBrand}</H1> : null}
            <H2 className={titleClasses}>{title}</H2>
          </div>
          <div className={css.authorContainer}>
            <AvatarMedium user={author} />
            <p className={css.listedLabel}>
              <FormattedMessage
                id="OrderPanel.OrderPanel.listedByLabel"
                values={{ authorDisplayName: authorDisplayName }}
              />
            </p>
          </div>
        </div>
        <div className={css.orderHeading}>
          {brandObject ? <H2 className={titleClasses}>{ifOtherBrand}</H2> : null}
          <div className={css.authorContainer}>
            <AvatarMedium user={author} className={css.providerAvatar} />
            <p className={css.listedLabel}>
              {' '}
              <NamedLink name="ProfilePage" params={{ id: userName }}>
                <FormattedMessage
                  id="OrderPanel.OrderPanel.listedByLabel"
                  values={{ authorDisplayName: authorDisplayName }}
                />
              </NamedLink>
            </p>
          </div>
        </div>
        <div>
          {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
          {/* {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null} */}
        </div>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          handleShowBuyerFeesModal={props.handleShowBuyerFeesModal}
          isPopnouvelleUser={isPopnouvelleUser}
          salePrice={salePrice}
        />
        {conditionLabel ? (
          <div className={css.container}>
            <p className={css.text}>
              <span className={css.label}> {conditionLabel}</span>
              <span className={css.moreInfo} onClick={() => handleShowCondition(true)}>
                {intl.formatMessage({ id: 'OrderPanel.OrderPanel.moreInfo' })}
              </span>
            </p>
          </div>
        ) : null}
        {colourObject ? (
          <div className={css.container}>
            <p className={css.text}>
              {/* <FormattedMessage id="ListingPage.colorTitle" />
              {': '} */}
              <span className={css.label}> {colourObject.label}</span>
            </p>
          </div>
        ) : null}
        {sizeObject ? (
          <div className={css.container}>
            <p className={css.text}>
              <FormattedMessage id="ListingPage.size" />
              {': '}
              <span className={css.label}> {sizeObject.label}</span>
            </p>
          </div>
        ) : null}
        {showAuthentication}
        {/* <div className={css.author}>
          <AvatarSmall user={author} className={css.providerAvatar} />
          <span className={css.providerNameLinked}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
          </span>
          <span className={css.providerNamePlain}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
          </span>
        </div> */}
        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onContactUser={onContactUser}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
            listing={listing}
            currentUser={currentUser}
            bottomAuthentication={bottomAuthentication}
            isClosed={isClosed}
            isCreatedByScript={isCreatedByScript}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>
      <div className={css.orderForm}>
        {showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onContactUser={onContactUser}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
            listing={listing}
            currentUser={currentUser}
            bottomAuthentication={bottomAuthentication}
            isClosed={isClosed}
            isCreatedByScript={isCreatedByScript}
            priceContainer={
              <PriceMaybe
                price={price}
                publicData={publicData}
                validListingTypes={validListingTypes}
                intl={intl}
                marketplaceCurrency={marketplaceCurrency}
                showCurrencyMismatch
                handleShowBuyerFeesModal={props.handleShowBuyerFeesModal}
                isPopnouvelleUser={isPopnouvelleUser}
                salePrice={salePrice}
              />
            }
          />
        ) : null}
      </div>
      {/* <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
          handleShowBuyerFeesModal={props.handleShowBuyerFeesModal}
        />

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm,
              onSubmit,
              history,
              location
            )}
            disabled={isOutOfStock}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton> 
        )}
      </div> */}
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  salePriceOverride: null,
  useSalePriceOverride: false,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  salePriceOverride: number,
  useSalePriceOverride: bool,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(OrderPanel);
